/**
 * One payment's receipt — itemized from the price basis frozen on the intent at
 * checkout. "Our record", not the tax invoice: the provider is merchant of
 * record and emails that. Ported in spirit from the web `ReceiptDialog`.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
} from "@fluentui/react-components";
import { type FC, useEffect, useState } from "react";

import { getIntent, type PaymentIntent } from "../../api/client";
import { openExternal } from "../services/openExternal";
import { fonts, inkColors } from "../tokens";
import { formatMoney, formatUsd } from "./money";
import { groupReceiptLines, paidAmount, subtotalMinor } from "./payments";

const useStyles = makeStyles({
  surface: { maxWidth: "420px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "8px" },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: inkColors.ink,
  },
  muted: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink3 },
  total: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "14px",
    fontWeight: 600,
    color: inkColors.ink,
    paddingTop: "6px",
    borderTop: "1px solid #ECE7F2",
  },
});

export const ReceiptDialog: FC<{ ref_: string | null; onDismiss: () => void }> = ({ ref_, onDismiss }) => {
  const styles = useStyles();
  const [intent, setIntent] = useState<PaymentIntent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ref_ === null) {
      setIntent(null);
      setError(null);
      return;
    }
    let live = true;
    getIntent(ref_)
      .then((i) => {
        if (live) setIntent(i);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : "Could not load the receipt.");
      });
    return () => {
      live = false;
    };
  }, [ref_]);

  const groups = intent ? groupReceiptLines(intent) : [];
  const tax = intent?.charged_tax_minor ?? null;

  return (
    <Dialog
      open={ref_ !== null}
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Receipt</DialogTitle>
          <DialogContent>
            {intent === null && error === null && <Spinner size="tiny" />}
            {error !== null && <span className={styles.muted}>{error}</span>}
            {intent !== null && (
              <div className={styles.body}>
                {groups.map((g) => (
                  <div className={styles.row} key={`${g.label} ${g.priceMinor}`}>
                    <span>
                      {g.count > 1 ? `${g.count} × ` : ""}
                      {g.label}
                    </span>
                    <span>{formatUsd(g.priceMinor * g.count)}</span>
                  </div>
                ))}
                <div className={styles.row}>
                  <span className={styles.muted}>Subtotal</span>
                  <span className={styles.muted}>{formatUsd(subtotalMinor(intent))}</span>
                </div>
                {tax !== null && intent.charged_currency !== null && (
                  <div className={styles.row}>
                    <span className={styles.muted}>Tax</span>
                    <span className={styles.muted}>{formatMoney(tax, intent.charged_currency)}</span>
                  </div>
                )}
                <div className={styles.total}>
                  <span>Paid</span>
                  <span>{paidAmount(intent)}</span>
                </div>
                {intent.provider_payment_id !== null && (
                  <span className={styles.muted}>Reference: {intent.provider_payment_id}</span>
                )}
                <span className={styles.muted}>
                  Dodo Payments is the merchant of record and emails the tax invoice. This is our record of the sale.
                </span>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {intent?.provider_receipt_url && (
              <Button appearance="secondary" onClick={() => openExternal(intent.provider_receipt_url as string)}>
                Provider receipt
              </Button>
            )}
            <Button appearance="primary" onClick={onDismiss}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ReceiptDialog;
